///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthPosition } from '../../position';
import { GtthHistoryService } from '../../../engine/history-service';
import { GtthStepGroupEnum } from '../../../../../generated/shared-types.model';
import { TopologyLinkTypeEnum } from '../../../../../services/project-socket/dto/topology-types.model';
import { E2eTestUtils } from '../../../../../../testing/e2e-test-utils';
import { LinksColors } from './links-colors.model';
import { TestMarkerView } from '../../marker/test/test-marker-view';

export class LinkFeatureView {
    private static readonly NOT_ACTIVE_LAYER_OPACITY = 0.3;
    line: google.maps.Polyline;
    bottomLine: google.maps.Polyline;
    testMarker: TestMarkerView;
    private readonly lineColors: string[];
    private lineSelectedColor: string;
    private lineUserForcedColor: string;
    private lineInstalledColor: string;
    private linkDisableColor: string;

    private path: any[];
    private linkName: string;
    private linkTrueStatus: TopologyLinkTypeEnum;
    private linkCurrentStatus: TopologyLinkTypeEnum;
    private isSelected: boolean;
    private isDisabled: boolean;
    private isUserForced: boolean;
    private isInstalled: boolean;
    private customColor: string;

    private lastLinkStatus: TopologyLinkTypeEnum;
    private lastLineColor: string;
    private lastLineOpacity: number;

    private linkOpacity: number;

    constructor(
        private readonly historyService: GtthHistoryService,
        private readonly fromPosition: GtthPosition,
        private readonly toPosition: GtthPosition
    ) {
        this.lineColors = [];
        this.path = [
            { lat: fromPosition.latitude, lng: fromPosition.longitude },
            { lat: toPosition.latitude, lng: toPosition.longitude }
        ];
    }

    refreshLink() {
        if (this.line) {
            const linkStatus = this.calculateLineState();
            const lineColor = this.calculateLineColor(linkStatus);

            if (this.lastLinkStatus !== linkStatus || this.lastLineColor !== lineColor || this.lastLineOpacity !== this.linkOpacity) {
                this.lastLinkStatus = linkStatus;
                this.lastLineColor = lineColor;
                this.lastLineOpacity = this.linkOpacity;
                this.line.setOptions(this.getPolylineOptions(this.lastLinkStatus, this.lastLineColor, this.linkOpacity));
            }
        }
    }

    draw(): boolean {
        this.setColors();

        if (this.line) {
            return false;
        }

        this.lastLinkStatus = this.calculateLineState();
        this.lastLineColor = this.calculateLineColor(this.lastLinkStatus);

        this.line = new google.maps.Polyline(this.getPolylineOptions(this.lastLinkStatus, this.lastLineColor, this.linkOpacity));
        this.line.setMap(this.historyService.map);

        this.bottomLine = new google.maps.Polyline({
            visible: true,
            draggable: false,
            editable: false,
            clickable: true,
            strokeOpacity: 0,
            strokeWeight: 10,
            zIndex: 400,
            icons: [],
            path: this.path
        });
        this.bottomLine.setMap(this.historyService.map);

        if (E2eTestUtils.getShowLineMarkers()) {
            const position = GtthPosition.fromValues((this.path[0].lng + this.path[1].lng) / 2, (this.path[0].lat + this.path[1].lat) / 2);
            this.testMarker = new TestMarkerView(position.toLatLngLiteral(), `${E2eTestUtils.getShowLineMarkers()} ${this.linkName}`);
            this.testMarker.draw(this.historyService.map);
        }

        return true;
    }

    public setLinkName(value: string) {
        this.linkName = value;
    }

    public setLinkTrueStatus(value: TopologyLinkTypeEnum) {
        this.linkTrueStatus = value;
    }

    public setLinkCurrentStatus(value: TopologyLinkTypeEnum) {
        this.linkCurrentStatus = value;
    }

    public setIsSelected(value: boolean) {
        this.isSelected = value;
    }

    public setIsDisabled(value: boolean) {
        this.isDisabled = value;
    }

    public setIsUserForced(value: boolean) {
        this.isUserForced = value;
    }

    public setIsInstalled(value: boolean) {
        this.isInstalled = value;
    }

    public setColor(value: string) {
        this.customColor = value;
    }

    public setIsInActiveLayer(isInActiveLayer: boolean): void {
        this.linkOpacity = isInActiveLayer ? 1 : LinkFeatureView.NOT_ACTIVE_LAYER_OPACITY;
    }

    hide() {
        if (this.line) {
            this.line.setMap(null);
            this.line = undefined;
        }
        if (this.bottomLine) {
            this.bottomLine.setMap(null);
            this.bottomLine = undefined;
        }
        if (this.testMarker) {
            this.testMarker.hide();
            this.testMarker = undefined;
        }
    }

    registerClickListener(callback: (event: google.maps.MapMouseEvent) => any): void {
        this.bottomLine?.addListener('click', callback);
        this.testMarker?.registerClickListener(callback);
    }

    registerRightClickListener(callback: (event: google.maps.MapMouseEvent) => any): void {
        this.bottomLine?.addListener('rightclick', callback);
        this.testMarker?.registerRightClickListener(callback);
    }

    destroyListeners() {
        if (this.bottomLine) {
            google.maps.event.clearInstanceListeners(this.bottomLine);
        }
    }

    setNewPath(fromPosition: GtthPosition, toPosition: GtthPosition): void {
        this.path = [
            { lat: fromPosition.latitude, lng: fromPosition.longitude },
            { lat: toPosition.latitude, lng: toPosition.longitude }
        ];

        if (this.line) {
            this.line.setPath(this.path);
        }

        if (this.bottomLine) {
            this.bottomLine.setPath(this.path);
        }

        if (this.testMarker) {
            const position = GtthPosition.fromValues((this.path[0].lng + this.path[1].lng) / 2, (this.path[0].lat + this.path[1].lat) / 2);
            this.testMarker.setPosition(position.toLatLngLiteral());
        }
    }

    private getPolylineOptions(linkStatus: TopologyLinkTypeEnum, lineColor: string, opacity: number): google.maps.PolylineOptions {
        let lineOptions: any;

        if (linkStatus === TopologyLinkTypeEnum.NOT_CONFIRMED_LOS) {
            const lineSymbol = {
                path: 'M 0,-1 0,1',
                strokeColor: lineColor,
                strokeOpacity: opacity,
                scale: 2
            };

            lineOptions = {
                path: this.path,
                strokeWidth: 5,
                strokeOpacity: 0,
                zIndex: 200,
                icons: [
                    {
                        icon: lineSymbol,
                        offset: '0',
                        repeat: '10px'
                    }
                ]
            };
        } else {
            lineOptions = {
                visible: true,
                draggable: false,
                editable: false,
                clickable: true,
                strokeColor: lineColor,
                strokeOpacity: opacity,
                strokeWeight: linkStatus === TopologyLinkTypeEnum.LINK ? 4 : 2,
                icons: [],
                path: this.path,
                zIndex: 400
            };
        }

        return lineOptions;
    }

    private calculateLineState(): TopologyLinkTypeEnum {
        if (this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES && !this.isUserForced) {
            return this.linkTrueStatus;
        } else {
            return this.linkCurrentStatus;
        }
    }

    private calculateLineColor(linkStatus: TopologyLinkTypeEnum): string {
        if (this.isSelected) {
            return this.lineSelectedColor;
        }

        if (this.historyService.projectUserSessionService.isUseEngineColors && linkStatus === TopologyLinkTypeEnum.LINK) {
            return this.customColor;
        }

        if (this.isDisabled) {
            return this.linkDisableColor;
        } else if (this.isUserForced) {
            return this.isInstalled ? this.lineInstalledColor : this.lineUserForcedColor;
        } else {
            return this.lineColors[linkStatus];
        }
    }

    private setColors() {
        this.lineColors[TopologyLinkTypeEnum.LINK] = LinksColors.link;
        this.lineColors[TopologyLinkTypeEnum.NOT_CONFIRMED_LOS] = LinksColors.notConfirmedLOS;
        this.lineColors[TopologyLinkTypeEnum.CONFIRMED_LOS] = LinksColors.confirmedLOS;
        this.lineColors[TopologyLinkTypeEnum.NO_LOS] = LinksColors.noLOS;
        this.lineColors[TopologyLinkTypeEnum.FIBER] = LinksColors.fiber;
        this.lineSelectedColor = LinksColors.selected;
        this.lineUserForcedColor = LinksColors.frozen;
        this.lineInstalledColor = LinksColors.installed;
        this.linkDisableColor = LinksColors.disabled;
    }
}
